import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import api, { API_BASE_URL } from "../../src/utils/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage("Invalid or expired token.");
      setOpenSnackbar(true);
    }
  }, [token]);

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setErrorMessage("Please enter all fields.");
      setSuccessMessage("");
      setOpenSnackbar(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setSuccessMessage("");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`${API_BASE_URL}/reset-password`, {
        token,
        newPassword,
      });

      if (response.status === 200) {
        setSuccessMessage("Password reset successfully. Redirecting to login...");
        setErrorMessage("");
        setOpenSnackbar(true);

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to reset password.");
      }
    } catch (error) {
      setSuccessMessage("");
      setErrorMessage(error.response?.data?.message || "An error occurred.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        backgroundImage: "url('/hoa.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        px: 2,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.18), rgba(0,0,0,0.32))",
        }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "420px",
          px: 4,
          pt: 3,
          pb: 4,
          backgroundColor: "rgba(255,255,255,0.95)",
          borderRadius: "18px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          textAlign: "center",
          position: "relative",
          zIndex: 2,
          backdropFilter: "blur(6px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1.25rem",
            marginTop: "0.25rem",
          }}
        >
          <img
            src="/img/logo.png"
            alt="Logo"
            style={{
              width: "230px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#1f1f1f",
            mb: 0.75,
          }}
        >
          Reset Password
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#666",
            mb: 2.25,
          }}
        >
          Enter your new password below.
        </Typography>

        {!token ? (
          <Typography
            sx={{
              color: "#d32f2f",
              fontWeight: 600,
              mt: 1,
            }}
          >
            Invalid or expired token.
          </Typography>
        ) : (
          <>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{
                marginBottom: "1rem",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.95)",
                  borderRadius: "10px",
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{
                marginBottom: "1.25rem",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.95)",
                  borderRadius: "10px",
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleResetPassword}
              disabled={loading}
              sx={{
                backgroundColor: "#2f63ad",
                color: "white",
                fontWeight: 700,
                fontSize: "1rem",
                padding: "0.85rem",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1f4f94",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#7ea1d6",
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Reset Password"
              )}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={() => navigate("/login")}
              sx={{
                mt: 1.25,
                color: "#2f63ad",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Back to Login
            </Button>
          </>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={successMessage ? "success" : "error"}
        >
          {successMessage || errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPassword;